import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { useActivityTracker } from "./hooks/useActivityTracker";
import PendingPaymentBanner from "./components/PendingPaymentBanner";
import AddressValidationBanner from "./components/AddressValidationBanner";
import ProfileValidationModal, { getProfileValidationIssues } from "./components/ProfileValidationModal";
import RestrictedAccountModal from "./components/RestrictedAccountModal";
import Landing from "./pages/Landing";
import Catalog from "./pages/Catalog";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminRevenue from "./pages/AdminRevenue";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminCustomers from "./pages/AdminCustomers";
import AdminBids from "./pages/AdminBids";
import AdminTracking from "./pages/AdminTracking";
import AdminMailingList from "./pages/AdminMailingList";
import AdminProductImport from "./pages/AdminProductImport";
import AdminContactMessages from "./pages/AdminContactMessages";
import AdminLive from "./pages/AdminLive";
import AdminIPIntelligence from "./pages/AdminIPIntelligence";
import AdminEmailProcessor from "./pages/AdminEmailProcessor";
import AdminOperatingCosts from "./pages/AdminOperatingCosts";
import AdminCarts from "./pages/AdminCarts";
import PrintLabels from "./pages/PrintLabels";
import AdminPromoCodes from "./pages/AdminPromoCodes";
import Admin2FA from "./pages/Admin2FA";
import AdminShipments from "./pages/AdminShipments";
import AdminBLKCRD from "./pages/AdminBLKCRD";
import AdminSubmissions from "./pages/AdminSubmissions";
import AdminProfileReview from "./pages/AdminProfileReview";
import AdminCrashes from "./pages/AdminCrashes.jsx";
import AdminInbound from "./pages/AdminInbound.jsx";
import AdminConsignment from "./pages/AdminConsignment.jsx";
import MyConsignments from "./pages/MyConsignments.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import { installCrashInterceptor } from "./lib/crashInterceptor";
import SubmitItems from "./pages/SubmitItems";
import Unsubscribe from "./pages/Unsubscribe";
import NotFound from "./pages/NotFound";
import "./App.css";

// Install global axios interceptor that reports 5xx + network failures to the crash tracker.
installCrashInterceptor();

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, authResolved, hasToken } = useAuth();
  
  // If token exists, render immediately - don't wait for auth
  // This eliminates the "LOADING_" flash on refresh
  if (hasToken && !adminOnly) {
    return children;
  }
  
  // For admin routes, we need to verify admin status
  if (adminOnly) {
    // If still loading and has token, show loading (need to verify admin)
    if ((loading || !authResolved) && hasToken) {
      return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center">
          <div className="text-tiffany-500 font-mono">LOADING_</div>
        </div>
      );
    }
    
    // No token = login
    if (!hasToken) {
      return <Navigate to="/login" />;
    }
    
    // Has token but user not loaded yet = keep waiting
    if (!user) {
      return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center">
          <div className="text-tiffany-500 font-mono">LOADING_</div>
        </div>
      );
    }
    
    // User loaded but not admin = redirect
    if (!user.is_admin) {
      return <Navigate to="/" />;
    }
    
    return children;
  }
  
  // Non-admin route, no token = login
  if (!hasToken) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Activity tracker component - runs on every page
const ActivityTracker = ({ children }) => {
  useActivityTracker();
  return children;
};

// Restricted account checker - blocks site completely for banned/restricted users
const RestrictedAccountChecker = ({ children }) => {
  const { user, userProfile, loading } = useAuth();

  // Show restricted modal if user is logged in and restricted
  if (!loading && user && userProfile?.is_restricted) {
    return <RestrictedAccountModal restrictionReason={userProfile.restriction_reason} />;
  }

  return children;
};

// Profile validation checker - blocks site ONLY if admin has flagged the user
const ProfileValidationChecker = ({ children }) => {
  const { user, userProfile, getAuthHeader, refreshProfile, loading } = useAuth();
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    const checkProfileFlag = async () => {
      // Only check after auth has loaded and user is logged in
      if (!loading && user && !hasChecked) {
        // Skip validation for admins - they need full site access
        if (user.is_admin) {
          setHasChecked(true);
          return;
        }
        
        try {
          // Check if user is flagged by admin
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile-status`, {
            headers: getAuthHeader()
          });
          const data = await response.json();
          
          if (data.profile_flagged) {
            setFlagReason(data.flag_reason || 'Profile requires verification');
            setShowValidationModal(true);
          }
        } catch (error) {
          console.error('Error checking profile status:', error);
        }
        setHasChecked(true);
      }
    };
    
    checkProfileFlag();
  }, [loading, user, hasChecked, getAuthHeader]);

  // Reset check when user changes
  useEffect(() => {
    setHasChecked(false);
    setShowValidationModal(false);
  }, [user?.id]);

  const handleValidated = async () => {
    await refreshProfile();
    setShowValidationModal(false);
    setHasChecked(false); // Re-check after save
  };

  return (
    <>
      {showValidationModal && userProfile && (
        <ProfileValidationModal
          userProfile={userProfile}
          getAuthHeader={getAuthHeader}
          onValidated={handleValidated}
          flagReason={flagReason}
        />
      )}
      {children}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <ActivityTracker>
            <RestrictedAccountChecker>
              <ProfileValidationChecker>
                <div className="min-h-screen bg-[#050505]">
                  <PendingPaymentBanner />
                  <AddressValidationBanner />
                  <div className="blkmrkt-bg"></div>
                  <div className="noise-overlay"></div>
                  <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/catalog" element={
                    <ProtectedRoute>
                      <Catalog />
                    </ProtectedRoute>
                  } />
                  <Route path="/product/:id" element={
                    <ProtectedRoute>
                      <ProductDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/cart" element={
                    <ProtectedRoute>
                      <Cart />
                    </ProtectedRoute>
                  } />
                  <Route path="/checkout" element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                } />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verify/:token" element={<VerifyEmail />} />
              <Route path="/unsubscribe/:email" element={<Unsubscribe />} />
              <Route path="/submit-items" element={
                <ProtectedRoute>
                  <SubmitItems />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute adminOnly>
                  <AdminUsers />
                </ProtectedRoute>
              } />
              <Route path="/admin/revenue" element={
                <ProtectedRoute adminOnly>
                  <AdminRevenue />
                </ProtectedRoute>
              } />
              <Route path="/admin/analytics" element={
                <ProtectedRoute adminOnly>
                  <AdminAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/admin/customers" element={
                <ProtectedRoute adminOnly>
                  <AdminCustomers />
                </ProtectedRoute>
              } />
              <Route path="/admin/bids" element={
                <ProtectedRoute adminOnly>
                  <AdminBids />
                </ProtectedRoute>
              } />
              <Route path="/admin/tracking" element={
                <ProtectedRoute adminOnly>
                  <AdminTracking />
                </ProtectedRoute>
              } />
              <Route path="/admin/mailing-list" element={
                <ProtectedRoute adminOnly>
                  <AdminMailingList />
                </ProtectedRoute>
              } />
              <Route path="/admin/product-import" element={
                <ProtectedRoute adminOnly>
                  <AdminProductImport />
                </ProtectedRoute>
              } />
              <Route path="/admin/contact-messages" element={
                <ProtectedRoute adminOnly>
                  <AdminContactMessages />
                </ProtectedRoute>
              } />
              <Route path="/admin/live" element={
                <ProtectedRoute adminOnly>
                  <AdminLive />
                </ProtectedRoute>
              } />
              <Route path="/admin/ip-intelligence" element={
                <ProtectedRoute adminOnly>
                  <AdminIPIntelligence />
                </ProtectedRoute>
              } />
              <Route path="/admin/email-processor" element={
                <ProtectedRoute adminOnly>
                  <AdminEmailProcessor />
                </ProtectedRoute>
              } />
              <Route path="/admin/operating-costs" element={
                <ProtectedRoute adminOnly>
                  <AdminOperatingCosts />
                </ProtectedRoute>
              } />
              <Route path="/admin/carts" element={
                <ProtectedRoute adminOnly>
                  <AdminCarts />
                </ProtectedRoute>
              } />
              <Route path="/admin/print-labels" element={
                <ProtectedRoute adminOnly>
                  <PrintLabels />
                </ProtectedRoute>
              } />
              <Route path="/admin/promo-codes" element={
                <ProtectedRoute adminOnly>
                  <AdminPromoCodes />
                </ProtectedRoute>
              } />
              <Route path="/admin/2fa" element={
                <ProtectedRoute adminOnly>
                  <Admin2FA />
                </ProtectedRoute>
              } />
              <Route path="/admin/shipments" element={
                <ProtectedRoute adminOnly>
                  <AdminShipments />
                </ProtectedRoute>
              } />
              <Route path="/admin/blkcrd" element={
                <ProtectedRoute adminOnly>
                  <AdminBLKCRD />
                </ProtectedRoute>
              } />
              <Route path="/admin/submissions" element={
                <ProtectedRoute adminOnly>
                  <AdminSubmissions />
                </ProtectedRoute>
              } />
              <Route path="/admin/profile-review" element={
                <ProtectedRoute adminOnly>
                  <AdminProfileReview />
                </ProtectedRoute>
              } />
              <Route path="/admin/crashes" element={
                <ProtectedRoute adminOnly>
                  <AdminCrashes />
                </ProtectedRoute>
              } />
              <Route path="/admin/inbound" element={
                <ProtectedRoute adminOnly>
                  <AdminInbound />
                </ProtectedRoute>
              } />
              <Route path="/admin/consignment" element={
                <ProtectedRoute adminOnly>
                  <AdminConsignment />
                </ProtectedRoute>
              } />
              <Route path="/my-consignments" element={
                <ProtectedRoute>
                  <MyConsignments />
                </ProtectedRoute>
              } />
              {/* 404 - Catch all unmatched routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster 
              position="bottom-right" 
              duration={1500}
              toastOptions={{
                style: {
                  background: '#18181b',
                  color: '#fafafa',
                  border: '1px solid #3f3f46',
                  borderRadius: '0',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '11px',
                  padding: '6px 14px',
                  minHeight: 'unset',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  zIndex: 100,
                },
              }}
              style={{ zIndex: 100 }}
            />
            </div>
              </ProfileValidationChecker>
            </RestrictedAccountChecker>
          </ActivityTracker>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
